(() => {
  const providers = [
    {
      key: 'klarna',
      brand: 'Klarna',
      compactClass: 'ypb-klarna-compact',
      hiddenClass: 'ypb-klarna-native-hidden',
      selectors: [
        'klarna-placement',
        '[data-key*="credit-promotion"]',
        '[class*="klarna" i]',
        '[id*="klarna" i]'
      ]
    },
    {
      key: 'afterpay',
      brand: 'afterpay',
      compactClass: 'ypb-afterpay-compact',
      hiddenClass: 'ypb-afterpay-native-hidden',
      selectors: [
        'afterpay-placement',
        'square-placement',
        '[class*="afterpay" i]',
        '[id*="afterpay" i]',
        '[class*="clearpay" i]',
        '[id*="clearpay" i]'
      ]
    }
  ];

  let scheduled = false;

  function isProductPage() {
    return document.body?.dataset?.pageType === 'product';
  }

  function getMainProductScope() {
    return document.querySelector('main .shopify-section.relative.z-layout') || document.querySelector('main');
  }

  function getDeepText(element) {
    if (!element) return '';

    let text = element.textContent || '';
    const seenRoots = new Set();

    function addShadowText(root) {
      if (!root || seenRoots.has(root)) return;
      seenRoots.add(root);
      text += ` ${root.textContent || ''}`;
      root.querySelectorAll?.('*').forEach((child) => {
        if (child.shadowRoot) addShadowText(child.shadowRoot);
      });
    }

    if (element.shadowRoot) addShadowText(element.shadowRoot);
    element.querySelectorAll?.('*').forEach((child) => {
      if (child.shadowRoot) addShadowText(child.shadowRoot);
    });

    return text.replace(/\s+/g, ' ').trim();
  }

  function extractPaymentCount(text) {
    const normalized = String(text || '').replace(/\s+/g, ' ').trim();

    const numberMatch = normalized.match(/\b(\d+)\s+(?:interest[- ]free\s+)?(?:payments?|installments?|instalments?)/i);
    if (numberMatch) return numberMatch[1];

    const payInMatch = normalized.match(/\bpay\s+in\s+(\d+)\b/i);
    if (payInMatch) return payInMatch[1];

    const wordMap = {
      one: '1',
      two: '2',
      three: '3',
      four: '4',
      five: '5',
      six: '6',
      seven: '7',
      eight: '8'
    };
    const wordMatch = normalized.match(/\b(one|two|three|four|five|six|seven|eight)\s+(?:interest[- ]free\s+)?(?:payments?|installments?|instalments?)/i);
    if (wordMatch) return wordMap[wordMatch[1].toLowerCase()] || null;

    return null;
  }

  function isCustomElement(element) {
    return Boolean(element?.closest?.('.ypb-payment-compact'));
  }

  function looksLikeProvider(element, provider) {
    if (!element || isCustomElement(element)) return false;

    const tagName = element.tagName?.toLowerCase() || '';
    if (provider.key === 'klarna' && tagName === 'klarna-placement') return true;
    if (provider.key === 'afterpay' && (tagName === 'afterpay-placement' || tagName === 'square-placement')) return true;

    const text = getDeepText(element).toLowerCase();
    if (provider.key === 'afterpay') return text.includes('afterpay') || text.includes('clearpay');
    return text.includes('klarna');
  }

  function findPlacement(scope, provider) {
    if (!scope) return null;

    const candidates = [];
    const seen = new Set();

    provider.selectors.forEach((selector) => {
      scope.querySelectorAll(selector).forEach((element) => {
        if (seen.has(element)) return;
        seen.add(element);
        candidates.push(element);
      });
    });

    return candidates.find((element) => looksLikeProvider(element, provider)) || null;
  }

  function findNativeWrapper(placement) {
    if (!placement) return null;

    const appBlock = placement.closest?.('.shopify-app-block, [id^="shopify-block-"]');
    if (appBlock && !isCustomElement(appBlock)) return appBlock;

    const parent = placement.parentElement;
    if (parent && !isCustomElement(parent)) return parent;

    return placement;
  }

  function buildCopy(count) {
    return count ? `Or ${count} interest-free payments with` : 'Interest-free payments with';
  }

  function createCompactLine(provider, count) {
    const line = document.createElement('div');
    line.className = `ypb-payment-compact ${provider.compactClass}`;
    line.dataset.ypbPaymentProvider = provider.key;
    line.setAttribute('role', 'note');

    const copy = document.createElement('span');
    copy.className = 'ypb-payment-compact__copy';
    copy.textContent = buildCopy(count);

    const badge = document.createElement('span');
    badge.className = `ypb-payment-compact__badge ypb-payment-compact__badge--${provider.key}`;
    badge.setAttribute('aria-label', provider.brand);
    badge.textContent = provider.brand;

    const info = document.createElement('span');
    info.className = 'ypb-payment-compact__info';
    info.setAttribute('aria-hidden', 'true');
    info.textContent = 'ⓘ';

    line.append(copy, badge, info);
    return line;
  }

  function updateCompactLine(line, provider, count) {
    const copy = line.querySelector('.ypb-payment-compact__copy');
    if (copy) copy.textContent = buildCopy(count);
    line.setAttribute(
      'aria-label',
      count
        ? `${provider.brand}: ${count} interest-free payments`
        : `${provider.brand}: interest-free payments`
    );
  }

  function clearProvider(scope, provider) {
    scope?.querySelector?.(`[data-ypb-payment-provider="${provider.key}"]`)?.remove();
    scope?.querySelectorAll?.(`.${provider.hiddenClass}`).forEach((element) => {
      element.classList.remove(provider.hiddenClass);
      element.removeAttribute('aria-hidden');
    });
  }

  function decorateProvider(scope, provider) {
    const placement = findPlacement(scope, provider);
    if (!placement) {
      clearProvider(scope, provider);
      return;
    }

    const nativeWrapper = findNativeWrapper(placement);
    if (!nativeWrapper || isCustomElement(nativeWrapper)) return;

    const nativeText = getDeepText(nativeWrapper) || getDeepText(placement);
    const count = extractPaymentCount(nativeText);

    let compact = scope.querySelector(`[data-ypb-payment-provider="${provider.key}"]`);
    if (!compact) {
      compact = createCompactLine(provider, count);
      nativeWrapper.insertAdjacentElement('afterend', compact);
    }

    updateCompactLine(compact, provider, count);
    nativeWrapper.classList.add(provider.hiddenClass);
    nativeWrapper.setAttribute('aria-hidden', 'true');
  }

  function decorate() {
    scheduled = false;
    if (!isProductPage()) return;

    const scope = getMainProductScope();
    if (!scope) return;

    providers.forEach((provider) => decorateProvider(scope, provider));
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(decorate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }

  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:block:select', schedule);

  const observer = new MutationObserver(schedule);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
    characterData: true
  });
})();
